/**
 * 반영되지 않은 항목을 워크북 셀에 붙이는 표시 한 벌.
 *
 * failures는 서버가 대응 IXD 셀을 못 찾아 실패한 것이라 IXD 쪽에는 붙일 자리가
 * 없다. 워크북 셀 주소로 좌측 표의 그 셀을 짚는다 — 고쳐야 할 자리도 거기다.
 *
 * 매칭된 행과 워크북 전용 행이 같은 표시를 써야 하므로 훅으로 묶는다. 한쪽에만
 * 붙이면 "어떤 표에서는 실패가 보이고 어떤 표에서는 안 보이는" 상태가 된다.
 */

import { useCallback, useId, useMemo, useRef, useState } from "react";

import { CellLinkagePopover } from "@/components/ixd-preview";
import type { WorkbookCellNotice } from "@/components/table-preview/table/workbook-cell-notice-context";
import {
  makeCellTagKey,
  periodScopedTagKey,
} from "@/utils/workbook/cell-tag-key";
import {
  describeFailure,
  failureTone,
  groupUnappliedValues,
  shortFailureLabel,
  type PlanTableDiagnostics,
} from "./plan-table-diagnostics";

/**
 * 진단 행을 셀에 붙일 표시로 바꾼다.
 *
 * 훅 밖에 두는 이유는 매칭 규칙이 이 기능의 핵심이라서다 — 어떤 실패가 어떤
 * 셀에 붙는지는 렌더링 없이 검증할 수 있어야 한다.
 */
export const buildWorkbookCellNotices = (
  diagnostics: PlanTableDiagnostics,
  tableName: string | undefined,
  /**
   * 셀에 달 키의 접두사. 한 화면에 표가 여러 개라 행 키만으로는 겹친다 —
   * 반영불가 패널이 자기 표의 셀만 정확히 찾아가려면 표마다 달라야 한다.
   */
  keyPrefix = "",
) => {
  const byAddress = new Map<string, WorkbookCellNotice>();
  const byTagKey = new Map<string, WorkbookCellNotice>();
  for (const row of diagnostics.rows) {
    /*
     * IXD에 짝이 없는 표는 셀마다 사유를 붙이지 않는다.
     *
     * 짚을 표가 아예 없는데 "합계열 사용이 체크되지 않았습니다" 같은 말을
     * 셀마다 붙이면, 사용자는 없는 표에서 체크박스를 찾게 된다. 이 표에서 할
     * 수 있는 말은 "IXD에 표 없음" 하나뿐이고 그건 표 배지가 맡는다.
     */
    if (!row.ixdTableKey) continue;

    const notice: WorkbookCellNotice = {
      key: `${keyPrefix}${row.key}`,
      reason: describeFailure(row, tableName),
      shortReason: shortFailureLabel(row),
      tone: failureTone(row.reason),
    };
    if (row.sourceAddress) {
      byAddress.set(row.sourceAddress, notice);
      continue;
    }
    /*
     * 주소가 없으면 셀의 태그 키로 짚는다. 기간까지 묶어야 당기·전기가
     * 갈린다 — 이 둘은 값도 사유도 같고 기간만 다른 실패로 흔히 온다.
     */
    if (!row.periodType) continue;
    byTagKey.set(
      periodScopedTagKey(
        row.periodType,
        makeCellTagKey({
          lineItemId: row.lineItemId,
          begend: row.begend,
          memberIds: row.memberIds,
          accumulatedType: row.accumulatedType,
        }),
      ),
      notice,
    );
  }
  return { notices: byAddress, noticesByTagKey: byTagKey };
};

export const useWorkbookCellNotices = (
  diagnostics: PlanTableDiagnostics,
  /** 문장에 넣을 한글 표 이름 */
  tableName: string | undefined,
) => {
  // 이 표만의 접두사. 셀과 패널이 같은 키를 봐야 패널이 그 셀로 데려갈 수 있다.
  const keyPrefix = `${useId()}:`;
  const { notices, noticesByTagKey } = useMemo(
    () => buildWorkbookCellNotices(diagnostics, tableName, keyPrefix),
    [diagnostics, tableName, keyPrefix],
  );
  /** 반영불가 배지를 펼쳤을 때 보여줄 목록. 셀 표시와 같은 재료로 만든다 */
  const unappliedGroups = useMemo(
    () => groupUnappliedValues(diagnostics, tableName, keyPrefix),
    [diagnostics, tableName, keyPrefix],
  );

  // 사유는 우측 IXD 셀과 같은 패널로 띄운다 — 같은 뜻이면 같은 모양이어야 한다.
  const anchorRef = useRef<HTMLElement | null>(null);
  const [opened, setOpened] = useState<{
    element: HTMLElement;
    reason: string;
  } | null>(null);

  const onNoticeClick = useCallback(
    (notice: WorkbookCellNotice, element: HTMLElement) => {
      anchorRef.current = element;
      setOpened({ element, reason: notice.reason });
    },
    [],
  );

  const popover = (
    <CellLinkagePopover
      cellPopover={
        opened
          ? { mode: "cell-notice", element: opened.element, identityKey: "" }
          : null
      }
      anchorRef={anchorRef}
      items={[]}
      notice={
        opened
          ? {
              tone: "blocked",
              description: opened.reason,
            }
          : null
      }
      onClose={() => setOpened(null)}
    />
  );

  return { notices, noticesByTagKey, unappliedGroups, onNoticeClick, popover };
};
